import { useCallback, useEffect, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { usePerm } from "../context/PermContext";
import { Field, Loading } from "./common";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Checkbox } from "./ui/checkbox";
import { Button } from "./ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { BerthTimeline } from "./BerthTimeline";

const needsSize = (t) => /grillo|trappa|pendino/i.test(t || "");
const isSub = (t) => /catena|grillo|trappa|pendino|braga/i.test(t || "");
const nostromoOnly = (t) => /catena|braga|grillo/i.test(t || "");
export const CERT_TEXT = "Certifico che la manutenzione è stata effettuata a regola d'arte.";

function Presence({ id, data, editable, enabled, onSaved }) {
  const [occupied, setOccupied] = useState(!!data.berth.occupied);
  const [boat, setBoat] = useState(data.berth.boat_name || "");
  const save = async () => {
    if (occupied && !boat.trim()) return toast.error("Inserisci il nome della barca");
    try { const r = await api(`/marina/berths/${id}/presence`, { method: "POST", body: { occupied, boat_name: boat.trim() } }); toast.success(r?.note || "Presenza aggiornata"); onSaved(); } catch (e) { toast.error(e.message); }
  };
  return (
    <div className="space-y-5">
      {!enabled && <p className="text-sm rounded-xl bg-amber-50 text-amber-800 p-3">Gestione presenze disabilitata dal Direttore (sola lettura).</p>}
      {editable ? (
        <div className="space-y-4 rounded-xl border p-4">
          <label className="flex items-center justify-between text-sm font-medium">{occupied ? "Occupato" : "Libero"}<Switch checked={occupied} onCheckedChange={setOccupied} data-testid="berth-occupied-switch" /></label>
          {occupied && <Field label="Nome barca"><Input value={boat} onChange={(e) => setBoat(e.target.value)} placeholder="Nome imbarcazione" data-testid="berth-boat-input" /></Field>}
          <Button onClick={save} className="rounded-full bg-[var(--teal)] w-full" data-testid="berth-presence-save">Salva presenza</Button>
        </div>
      ) : enabled && <p className="text-sm text-slate-500">Solo il personale abilitato può aggiornare.</p>}
      <div><h3 className="font-semibold text-[var(--navy)] mb-2">Storico movimenti</h3>
        {(data.movements || []).length === 0 ? <p className="text-sm text-slate-500">Nessun movimento registrato.</p> : (
          <div className="space-y-2" data-testid="berth-movements">{data.movements.map((m, i) => (
            <div key={m.id || i} className="text-sm border rounded-lg p-3"><p className="font-medium">{m.nome_barca || "—"}</p>
              <p className="text-xs text-slate-500">Check-in: {m.data_check_in || "—"}{m.data_check_out ? ` · Check-out: ${m.data_check_out}` : ""}{m.data_operazione ? ` · Agg. ${m.data_operazione}` : ""} {m.orario || ""} · {m.operatore || ""}</p></div>))}
          </div>)}
      </div>
    </div>
  );
}

function EditMaint({ id, m, onDone }) {
  const [f, setF] = useState({ note: m.note || "", data: m.data_intervento || "", dim: m.dimensione || "", qty: m.quantita != null ? String(m.quantita) : "" });
  const save = async () => {
    const body = { note: f.note.trim(), data_intervento: f.data.trim() };
    if (needsSize(m.tipo)) { body.dimensione = f.dim.trim(); body.quantita = Number(f.qty) || 0; }
    try { await api(`/marina/berths/${id}/maintenance/${m.id}`, { method: "PUT", body }); toast.success("Intervento aggiornato"); onDone(); } catch (e) { toast.error(e.message); }
  };
  return (
    <div className="space-y-3 mt-3 border-t pt-3">
      <Field label="Data (aaaa-mm-gg)"><Input value={f.data} onChange={(e) => setF({ ...f, data: e.target.value })} data-testid="maint-edit-date" /></Field>
      {needsSize(m.tipo) && <div className="grid grid-cols-2 gap-2"><Input value={f.dim} onChange={(e) => setF({ ...f, dim: e.target.value })} placeholder="Dimensione" /><Input value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} placeholder="Quantità" /></div>}
      <Textarea value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Note intervento..." data-testid="maint-edit-note" />
      <div className="flex gap-2"><Button size="sm" onClick={save} className="rounded-full bg-[var(--teal)]" data-testid="maint-edit-save">Salva</Button><Button size="sm" variant="ghost" onClick={() => onDone(false)}>Annulla</Button></div>
    </div>
  );
}

function Maintenance({ id, data, canMaint, canSub, canPontili, reload }) {
  const [sel, setSel] = useState([]);
  const [sizes, setSizes] = useState({});
  const [note, setNote] = useState("");
  const [cert, setCert] = useState(false);
  const [editId, setEditId] = useState(null);
  const restricted = sel.some(nostromoOnly);
  const toggle = (t) => setSel((p) => (p.includes(t) ? p.filter((x) => x !== t) : [...p, t]));
  const setSize = (t, k, v) => setSizes((p) => ({ ...p, [t]: { dim: "", qty: "", ...p[t], [k]: v } }));

  const add = async () => {
    if (!sel.length) return toast.error("Seleziona almeno una voce di intervento");
    if (sel.includes("Altro") && !note.trim()) return toast.error("Descrivi l'intervento (voce Altro)");
    for (const t of sel) { const s = sizes[t] || {}; if (needsSize(t) && (!(s.dim || "").trim() || !(Number(s.qty) > 0))) return toast.error(`Inserisci dimensione e quantità per "${t}"`); }
    if (restricted && !cert) return toast.error("Conferma la certificazione del Nostromo per proseguire");
    const items = sel.map((t) => (needsSize(t) ? { tipo: t, dimensione: sizes[t].dim.trim(), quantita: Number(sizes[t].qty) } : { tipo: t }));
    try { await api(`/marina/berths/${id}/maintenance`, { method: "POST", body: { items, note: note.trim(), certifica: restricted ? cert : false } }); toast.success("Intervento registrato"); setSel([]); setSizes({}); setNote(""); setCert(false); reload(); } catch (e) { toast.error(e.message); }
  };
  const del = async (m) => {
    if (!window.confirm(`Vuoi eliminare l'intervento "${m.tipo}" del ${m.data_intervento || ""}?`)) return;
    try { await api(`/marina/berths/${id}/maintenance/${m.id}`, { method: "DELETE" }); toast.success("Intervento eliminato"); reload(); } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-5">
      {canMaint && (
        <div className="space-y-3 rounded-xl border p-4">
          <h3 className="font-semibold text-[var(--navy)]">Nuovo intervento</h3>
          {(data.maint_types || []).filter((t) => (t === "Altro" ? true : isSub(t) ? canSub : canPontili)).map((t) => (
            <div key={t}>
              <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={sel.includes(t)} onCheckedChange={() => toggle(t)} data-testid={`maint-type-${t}`} />{t}{nostromoOnly(t) && <span className="tag tag-slate">Nostromo</span>}</label>
              {sel.includes(t) && needsSize(t) && <div className="grid grid-cols-2 gap-2 mt-2 ml-6"><Input value={sizes[t]?.dim || ""} onChange={(e) => setSize(t, "dim", e.target.value)} placeholder="Dimensione es. 12 mm" /><Input type="number" value={sizes[t]?.qty || ""} onChange={(e) => setSize(t, "qty", e.target.value)} placeholder="Quantità es. 2" /></div>}
            </div>))}
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={sel.includes("Altro") ? "Descrivi l'intervento" : "Note (opzionale)"} data-testid="maint-note-input" />
          {restricted && <label className="flex items-start gap-2 text-sm"><Checkbox checked={cert} onCheckedChange={setCert} data-testid="maint-cert-checkbox" />{data.cert_text || CERT_TEXT}</label>}
          <Button onClick={add} className="rounded-full bg-[var(--teal)] w-full" data-testid="maint-add-button">Registra intervento</Button>
        </div>
      )}
      <div><h3 className="font-semibold text-[var(--navy)] mb-2">Interventi registrati</h3>
        {(data.maintenance || []).length === 0 ? <p className="text-sm text-slate-500">Nessun intervento.</p> : (
          <div className="space-y-2" data-testid="berth-maint-list">{data.maintenance.map((m) => (
            <div key={m.id} className="text-sm border rounded-lg p-3">
              <div className="flex justify-between gap-2"><p className="font-medium">{m.tipo}</p>
                {canMaint && <div className="flex gap-1"><button onClick={() => setEditId(m.id)} className="text-slate-400 hover:text-[var(--navy)]" data-testid={`maint-edit-${m.id}`}><Pencil className="w-4 h-4" /></button><button onClick={() => del(m)} className="text-slate-400 hover:text-[var(--red)]" data-testid={`maint-delete-${m.id}`}><Trash2 className="w-4 h-4" /></button></div>}</div>
              {(m.dimensione || m.quantita != null) && <p className="text-xs text-slate-600">{m.dimensione ? `Dimensione: ${m.dimensione}` : ""}{m.quantita != null ? ` · Quantità: ${m.quantita}` : ""}</p>}
              {m.note && <p className="text-slate-600 mt-1">{m.note}</p>}
              <p className="text-xs text-slate-400 mt-1">{m.data_intervento} {m.orario || ""} · {m.operatore || ""}{m.certificazione ? " · certificato" : ""}</p>
              {editId === m.id && <EditMaint id={id} m={m} onDone={(ok) => { setEditId(null); if (ok !== false) reload(); }} />}
            </div>))}
          </div>)}
      </div>
    </div>
  );
}

export function BerthSheet({ id, onPresenceSaved }) {
  const { can } = usePerm();
  const [data, setData] = useState(null);
  const load = useCallback(() => api(`/marina/berths/${id}`).then(setData).catch((e) => toast.error(e.message || "Errore caricamento")), [id]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading />;
  const b = data.berth;
  const canEdit = data.can_edit && can("marina.presenze");
  const allowed = data.can_maint ?? data.can_edit;
  const canSub = allowed && can("marina.maint_sub");
  const canPontili = allowed && can("marina.maint_pontili");
  const canMaint = canSub || canPontili;
  const enabled = data.presence_enabled !== false;
  const color = data.color === "yellow" ? "tag-amber" : b.occupied ? "tag-red" : "tag-green";
  return (
    <div data-testid="berth-sheet">
      <h2 className="font-display text-2xl font-bold text-[var(--navy)]">Pontile {b.pontile} - {b.numero}</h2>
      <div className="flex gap-2 items-center mt-1 mb-5">{b.dimensione && <span className="text-sm text-slate-500">{b.dimensione}</span>}<span className={`tag ${color}`}>{data.color === "yellow" ? "Modificato oggi" : b.occupied ? `Occupato${b.boat_name ? ` · ${b.boat_name}` : ""}` : "Libero"}</span></div>
      <Tabs defaultValue="presenze">
        <TabsList className="mb-4"><TabsTrigger value="presenze" data-testid="berth-tab-presenze">Presenze</TabsTrigger><TabsTrigger value="manutenzione" data-testid="berth-tab-manutenzione">Manutenzione</TabsTrigger><TabsTrigger value="storico" data-testid="berth-tab-storico">Storico</TabsTrigger></TabsList>
        <TabsContent value="presenze"><Presence id={id} data={data} editable={canEdit && enabled} enabled={enabled} onSaved={() => { load(); onPresenceSaved?.(b.pontile); }} /></TabsContent>
        <TabsContent value="manutenzione"><Maintenance id={id} data={data} canMaint={canMaint} canSub={canSub} canPontili={canPontili} reload={load} /></TabsContent>
        <TabsContent value="storico"><BerthTimeline data={data} /></TabsContent>
      </Tabs>
    </div>
  );
}
